import {
  Brain,
  Zap,
  CheckCircle2,
  AlertCircle,
  Bot,
  MessageSquare,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AgentEvent, DeviceMessage } from './shared-types';

export function parseGroupEvent(event: AgentEvent): { padId: string; message: DeviceMessage } | null {
  const d = event.data;
  if (!d || !event.profile_id) return null;

  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const timestamp = new Date(d.timestamp || Date.now());

  switch (event.event) {
    case 'message':
      return {
        padId: event.profile_id,
        message: { id, content: d.message || d.content || '', type: 'message', timestamp },
      };
    case 'thought':
      return {
        padId: event.profile_id,
        message: { id, content: d.thought || '', type: 'thought', timestamp },
      };
    case 'action': {
      if (d.action_type === 'web_search') {
        let query = '';
        try {
          query = d.target ? (JSON.parse(d.target).query || '') : '';
        } catch { /* fall through with empty query */ }
        let content: string;
        if (d.status === 'executing') {
          content = query ? `Searching the web: ${query}` : 'Searching the web...';
        } else if (d.status === 'completed') {
          content = query ? `Got results for: ${query}` : 'Got search results';
        } else if (d.status === 'failed') {
          content = d.message || (query ? `Search failed for: ${query}` : 'Search failed');
        } else {
          content = d.message || query || 'Web search';
        }
        return {
          padId: event.profile_id,
          message: { id, content, type: 'web_search', timestamp },
        };
      }
      const parts = [`**${d.action_type || 'action'}**`];
      if (d.target) parts.push(`on ${d.target}`);
      if (d.value) parts.push(`: ${d.value}`);
      return {
        padId: event.profile_id,
        message: { id, content: parts.join(' '), type: 'action', timestamp },
      };
    }
    case 'progress':
      // Only surface the backend's reconnect pings ("Reconnected at step X") so
      // the card doesn't look stuck while waiting for the next action. Per-step
      // progress events are just step counters — their message would flood the feed.
      if (d.message && String(d.message).startsWith('Reconnected')) {
        return {
          padId: event.profile_id,
          message: { id, content: d.message, type: 'message', timestamp },
        };
      }
      return null;
    case 'result':
      return {
        padId: event.profile_id,
        message: { id, content: d.result || 'Task completed', type: 'result', timestamp },
      };
    case 'error':
      return {
        padId: event.profile_id,
        message: { id, content: d.error || 'An error occurred', type: 'error', timestamp },
      };
    default:
      return null;
  }
}

export function getMessageIcon(type: string) {
  switch (type) {
    case 'thought': return <Brain size={14} className="shrink-0" />;
    case 'action': return <Zap size={14} className="shrink-0" />;
    case 'web_search': return <Search size={14} className="shrink-0" />;
    case 'result': return <CheckCircle2 size={14} className="shrink-0" />;
    case 'error': return <AlertCircle size={14} className="shrink-0" />;
    case 'message': return <MessageSquare size={14} className="shrink-0" />;
    default: return <Bot size={14} className="shrink-0" />;
  }
}

export function getMessageStyle(type: string) {
  switch (type) {
    case 'thought': return 'bg-purple-500/10 border-purple-500/20';
    case 'action': return 'bg-blue-500/10 border-blue-500/20';
    case 'web_search': return 'bg-cyan-500/10 border-cyan-500/20';
    case 'result': return 'bg-green-500/10 border-green-500/20';
    case 'error': return 'bg-destructive/10 border-destructive/20';
    case 'message': return 'bg-primary/10 border-primary/20';
    default: return 'bg-muted border-muted';
  }
}

export function getStatusBadge(status: string) {
  const config: Record<string, { dot: string; pulse: boolean; label: string; cls: string }> = {
    running:   { dot: 'bg-green-500',    pulse: true,  label: 'running',   cls: 'text-green-600' },
    completed: { dot: 'bg-green-500',    pulse: false, label: 'completed', cls: 'text-green-600' },
    completed_with_issues: { dot: 'bg-amber-500', pulse: false, label: 'completed · issues', cls: 'text-amber-600' },
    failed:    { dot: 'bg-destructive',  pulse: false, label: 'failed',    cls: 'text-destructive' },
    stopped:   { dot: 'bg-muted-foreground', pulse: false, label: 'stopped', cls: 'text-muted-foreground' },
  };
  const c = config[status] ?? { dot: 'bg-muted-foreground/40', pulse: false, label: 'pending', cls: 'text-muted-foreground' };
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-[11px] font-medium", c.cls)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", c.dot, c.pulse && "animate-pulse")} />
      {c.label}
    </span>
  );
}
